// ============================================================================
// /test — Free discipline simulator. No signup. No login. Just input.
// Output: how much SnapChart would have saved you. Shareable result.
// The viral hook: people get a result they want to brag/cry about on Twitter.
// ============================================================================
import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/SafeIcon';
import logoSrc from '@/assets/snap-chart-logo-final.png';

const { FiArrowRight, FiShield, FiCopy, FiCheck, FiAlertTriangle, FiChevronDown, FiChevronUp } = FiIcons;

// ─────────────────────────────────────────────────────────────────────────
// HYPER-PRO simulation model
//
// 1) Vetoable rate is TIERED by win rate (lower WR = more vetoable trades).
//    Calibrated against published retail trading studies (e.g. FCA UK 2022,
//    ESMA 2018 CFD reports — both find 70-80% of retail traders lose money,
//    with the biggest leak being entries without setup confirmation).
//
// 2) Per-trade risk is validated against the 2% rule. Above 2% = warning.
//
// 3) Capital saved is CAPPED at account size (you cannot preserve more
//    capital than you have). Raw figure is shown in the methodology only.
//
// 4) Result is normalized to monthly ($ over 90d / 3) for realistic framing.
// ─────────────────────────────────────────────────────────────────────────
function simulate({ totalTrades, winRate, avgLoss, tradingCapital }) {
  const tt = Math.max(0, Math.min(10000, Number(totalTrades) || 0));
  const wr = Math.max(0, Math.min(100, Number(winRate) || 0));
  const al = Math.max(0, Math.min(100000, Number(avgLoss) || 0));
  const cap = Math.max(0, Math.min(100000000, Number(tradingCapital) || 0));

  // Tiered vetoable rate — pros are already filtering setups, less vetoable
  let vetoableRate, tierLabel;
  if (wr < 30)      { vetoableRate = 0.62; tierLabel = 'high-impulse trader'; }
  else if (wr < 45) { vetoableRate = 0.50; tierLabel = 'average retail trader'; }
  else if (wr < 60) { vetoableRate = 0.35; tierLabel = 'developing trader'; }
  else              { vetoableRate = 0.22; tierLabel = 'disciplined trader'; }

  const losers = Math.round(tt * (1 - wr / 100));
  const vetoes = Math.round(losers * vetoableRate);
  const capitalSavedRaw = vetoes * al;

  // Cap at account size (can't preserve more than you have)
  const capitalSaved = Math.min(capitalSavedRaw, cap);
  const pctSaved = cap > 0 ? Math.min((capitalSaved / cap) * 100, 100) : 0;
  const pctSavedRaw = cap > 0 ? (capitalSavedRaw / cap) * 100 : 0;
  const monthlySaved = Math.round(capitalSaved / 3); // 90 days = 3 months

  // Risk-per-trade sanity check
  const riskPerTrade = cap > 0 ? (al / cap) * 100 : 0;
  let riskFlag = 'pro';
  if (riskPerTrade > 5)      riskFlag = 'extreme';
  else if (riskPerTrade > 2) riskFlag = 'high';

  return {
    tt, wr, al, cap,
    losers, vetoes, vetoableRate, tierLabel,
    capitalSaved, capitalSavedRaw, pctSaved, pctSavedRaw, monthlySaved,
    riskPerTrade, riskFlag,
  };
}

const DisciplineTest = () => {
  const [form, setForm] = useState({ totalTrades: '', winRate: '', avgLoss: '', tradingCapital: '' });
  const [submitted, setSubmitted] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showMethod, setShowMethod] = useState(false);

  const result = useMemo(() => simulate(form), [form]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.totalTrades || !form.winRate || !form.avgLoss || !form.tradingCapital) return;
    setSubmitted(true);
  };

  // Share text — leads with monthly savings (the realistic, repeatable hit)
  // rather than the capped % which can be misleading.
  const shareText = `SnapChart AI would have saved me $${result.monthlySaved.toLocaleString()}/month across ${result.vetoes} bad trades it would have vetoed.\n\nThe AI that tells you when NOT to trade.\n\nRun your own simulation: snap-chart.ai/test`;

  const copyShare = () => {
    try {
      navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* noop */ }
  };

  const tweet = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black text-white pt-20 pb-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <img src={logoSrc} alt="" className="h-7 w-7" />
            <span className="text-sm font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">SnapChart AI</span>
          </Link>
          <span className="inline-block px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-[10px] font-mono uppercase tracking-[0.2em] text-amber-300 mb-4">
            free · no signup · 30 seconds
          </span>
          <h1 className="text-4xl md:text-6xl font-black tracking-tight leading-[1.05] mb-4">
            <span className="text-white">How much would discipline</span><br />
            <span className="bg-gradient-to-r from-purple-400 via-cyan-300 to-cyan-400 bg-clip-text text-transparent">have saved you?</span>
          </h1>
          <p className="text-base md:text-lg text-gray-400 max-w-xl mx-auto">
            Enter your last 90 days of stats. We'll show you what SnapChart's pre-trade veto would have preserved — in real dollars.
          </p>
        </div>

        {/* Form */}
        {!submitted && (
          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl border border-white/10 bg-gray-900/60 p-6 md:p-8 space-y-5">
            <Field label="Your trading capital (account size)" placeholder="e.g. 10000" value={form.tradingCapital}
              onChange={(v) => setForm({ ...form, tradingCapital: v })} suffix="$" />
            <Field label="Total trades (last 90 days)" placeholder="e.g. 80" value={form.totalTrades}
              onChange={(v) => setForm({ ...form, totalTrades: v })} suffix="trades" />
            <Field label="Your win rate" placeholder="e.g. 42" value={form.winRate}
              onChange={(v) => setForm({ ...form, winRate: v })} suffix="%" />
            <Field label="Average loss per losing trade" placeholder="e.g. 120" value={form.avgLoss}
              onChange={(v) => setForm({ ...form, avgLoss: v })} suffix="$" />
            <button type="submit"
              className="w-full py-4 rounded-xl bg-gradient-to-r from-cyan-400 to-purple-400 text-black font-bold text-lg hover:scale-[1.01] transition shadow-2xl shadow-cyan-500/30 flex items-center justify-center gap-2">
              Calculate <SafeIcon icon={FiArrowRight} />
            </button>
            <p className="text-[11px] text-gray-500 text-center font-mono">
              Calculation is local — your stats never leave your browser.
            </p>
          </motion.form>
        )}

        {/* Result */}
        {submitted && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl border-2 border-emerald-500/30 bg-gradient-to-br from-emerald-900/15 via-gray-900/80 to-cyan-900/15 p-8 md:p-12 text-center relative overflow-hidden">
            <div className="absolute -top-32 -right-32 w-72 h-72 bg-emerald-500/20 rounded-full blur-3xl" />
            <div className="absolute -bottom-32 -left-32 w-72 h-72 bg-cyan-500/15 rounded-full blur-3xl" />
            <div className="relative">
              <SafeIcon icon={FiShield} className="text-emerald-400 text-5xl mx-auto mb-4" />

              {/* Headline — monthly savings (realistic, repeatable) */}
              <p className="text-sm uppercase tracking-[0.2em] font-mono text-emerald-400 mb-3">
                SnapChart would have saved you
              </p>
              <div className="text-6xl md:text-8xl font-black font-mono text-emerald-400 tabular-nums mb-1 leading-none">
                ${result.monthlySaved.toLocaleString()}
              </div>
              <p className="text-sm font-mono uppercase tracking-[0.2em] text-emerald-400/70 mb-5">
                per month · over 90 days
              </p>

              {/* Secondary stats row */}
              <div className="grid grid-cols-3 gap-2 max-w-xl mx-auto mb-6">
                <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10">
                  <div className="text-xl md:text-2xl font-black font-mono text-white tabular-nums">${result.capitalSaved.toLocaleString()}</div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500 mt-0.5">total · 90d</div>
                </div>
                <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10">
                  <div className="text-xl md:text-2xl font-black font-mono text-white tabular-nums">{result.pctSaved.toFixed(0)}%</div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500 mt-0.5">of account</div>
                </div>
                <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10">
                  <div className="text-xl md:text-2xl font-black font-mono text-white tabular-nums">{result.vetoes}</div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500 mt-0.5">bad trades vetoed</div>
                </div>
              </div>

              {/* Risk flag — only if user is overleveraging */}
              {result.riskFlag !== 'pro' && (
                <div className={`flex items-start gap-2 max-w-xl mx-auto mb-5 p-3 rounded-xl border text-left ${result.riskFlag === 'extreme' ? 'bg-red-500/10 border-red-500/30 text-red-200' : 'bg-amber-500/10 border-amber-500/30 text-amber-200'}`}>
                  <SafeIcon icon={FiAlertTriangle} className="flex-shrink-0 mt-0.5 text-base" />
                  <div className="text-xs leading-relaxed">
                    <strong>Risk flag:</strong> you're risking <strong>{result.riskPerTrade.toFixed(1)}%</strong> of your account per losing trade.
                    {' '}Professional risk management caps this at <strong>1-2%</strong>. Your current setup is {result.riskFlag === 'extreme' ? 'extremely' : 'significantly'} above that threshold.
                  </div>
                </div>
              )}

              {/* Methodology — collapsible */}
              <button onClick={() => setShowMethod(!showMethod)}
                className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.15em] text-gray-500 hover:text-cyan-400 transition mb-3">
                <SafeIcon icon={showMethod ? FiChevronUp : FiChevronDown} />
                {showMethod ? 'hide' : 'show'} how we calculated this
              </button>

              {showMethod && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                  className="max-w-xl mx-auto mb-6 p-5 rounded-xl bg-black/40 border border-white/10 text-left text-sm">
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-cyan-400 mb-3">how we got here</div>
                  <ol className="space-y-2.5 text-gray-300">
                    <li className="flex gap-2">
                      <span className="text-cyan-400 font-mono text-xs flex-shrink-0 mt-0.5">01</span>
                      <span>You traded <strong className="text-white">{result.tt}</strong> times over 90 days with a <strong className="text-white">{result.wr}%</strong> win rate.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-cyan-400 font-mono text-xs flex-shrink-0 mt-0.5">02</span>
                      <span>Our discipline model identified <strong className="text-white">{result.vetoes}</strong> of your <strong className="text-white">{result.losers}</strong> losing trades as <strong className="text-cyan-300">vetoable</strong> (impulse entries, weak structure, wrong regime).</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-cyan-400 font-mono text-xs flex-shrink-0 mt-0.5">03</span>
                      <span>Those <strong className="text-white">{result.vetoes}</strong> avoided losses × <strong className="text-white">${result.al.toLocaleString()}</strong> avg = <strong className="text-emerald-300">${result.capitalSavedRaw.toLocaleString()}</strong> raw capital preserved.</span>
                    </li>
                    {result.capitalSavedRaw > result.cap && (
                      <li className="flex gap-2">
                        <span className="text-amber-400 font-mono text-xs flex-shrink-0 mt-0.5">04</span>
                        <span>Result capped at your <strong className="text-white">${result.cap.toLocaleString()}</strong> account size — you can't preserve more than you have.</span>
                      </li>
                    )}
                    <li className="flex gap-2">
                      <span className="text-cyan-400 font-mono text-xs flex-shrink-0 mt-0.5">{result.capitalSavedRaw > result.cap ? '05' : '04'}</span>
                      <span>Monthly average: <strong className="text-emerald-300">${result.monthlySaved.toLocaleString()}/month</strong> on a <strong className="text-white">${result.cap.toLocaleString()}</strong> account.</span>
                    </li>
                  </ol>
                  <p className="text-[10px] text-gray-500 font-mono mt-4 pt-3 border-t border-white/5">
                    Proprietary discipline model. Not financial advice — heuristic estimate only.
                  </p>
                </motion.div>
              )}

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-2 mb-3">
                <Link to="/register"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-cyan-400 to-purple-400 text-black font-bold hover:scale-105 transition shadow-xl shadow-cyan-500/30">
                  Get my AI Risk Officer <SafeIcon icon={FiArrowRight} />
                </Link>
                <a href={tweet} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-white/20 text-gray-200 hover:bg-white/5 transition">
                  <span className="font-black text-lg leading-none">𝕏</span> Share on X
                </a>
                <button onClick={copyShare}
                  title="Copy share text for TikTok / Instagram / Telegram / etc."
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-white/10 text-gray-300 hover:bg-white/5 transition">
                  <SafeIcon icon={copied ? FiCheck : FiCopy} /> {copied ? 'Copied' : 'Copy for IG/TikTok'}
                </button>
              </div>
              <button onClick={() => setSubmitted(false)}
                className="text-xs text-gray-500 hover:text-cyan-400 mt-3 font-mono">
                ← run another simulation
              </button>
            </div>
          </motion.div>
        )}

        <p className="mt-12 text-center text-xs text-gray-600 font-mono leading-relaxed">
          Not financial advice. This is a heuristic estimate based on industry trader-loss data,<br />
          not a guarantee of future results with the product.
        </p>
      </div>
    </div>
  );
};

function Field({ label, value, onChange, placeholder, suffix }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-300 mb-2">{label}</label>
      <div className="relative">
        <input type="number" value={value} onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full px-4 py-3 pr-16 bg-gray-900/70 border border-white/10 rounded-xl text-white text-lg font-mono focus:border-cyan-400/50 focus:outline-none" />
        {suffix && (
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-500 font-mono">{suffix}</span>
        )}
      </div>
    </div>
  );
}

export default DisciplineTest;
